import { useClient } from '@/context/ClientContext'
import { fetchAssetSets } from '@/lib/clientHelpers'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { z } from 'zod'

import {
  AssetSetType,
  AssetType,
  AssetWithPresignedUrl,
  PresignedUrlType,
} from '@droidmap/asset-service-contract'
import { Tabs, TabsList, TabsTrigger } from '@droidmap/frontend-ui'
import { Button } from '@droidmap/frontend-ui'

const compareSearchSchema = z.object({
  /** Asset-set id to seed as the first selection (e.g. coming from the
   *  asset-set detail "Compare" button). The compare type is auto-picked
   *  from the asset set's type. */
  preselectId: z.string().optional(),
})

export const Route = createFileRoute(
  '/_authenticated/orgs/$orgId_/projects/$projectId_/_pathless/compare',
)({
  component: CompareSelection,
  validateSearch: compareSearchSchema,
  loader: async ({ params, context }) => {
    return {
      metadata: context.metadata,
      projectId: params.projectId,
      orgId: params.orgId,
    }
  },
})

type CompareType = 'map' | 'image-comparison'

function CompareSelection() {
  const navigate = useNavigate()
  const { assetClient, projectClient } = useClient()
  const { metadata, projectId, orgId } = Route.useLoaderData()
  const { preselectId } = Route.useSearch()
  const [selectedCompareType, setSelectedCompareType] =
    useState<CompareType>('map')
  const [selectedAssetSets, setSelectedAssetSets] = useState<string[]>([])
  const [selectedAssets, setSelectedAssets] = useState<{
    left?: string
    right?: string
  }>({})
  const [step, setStep] = useState<'asset-sets' | 'assets'>('asset-sets')

  const { data: project } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => projectClient.getProject({ projectId }, metadata),
  })

  const assetSetsQuery = useQuery({
    queryKey: ['assetSets', projectId],
    queryFn: () => {
      if (!project) {
        return Promise.reject('Project data is not available yet.')
      }
      return fetchAssetSets([project], assetClient, metadata)
    },
    enabled: !!project,
  })

  // Apply ?preselectId=… from the asset-set detail's Compare button. Run once
  // per preselectId after asset sets land, and pick the compare type from the
  // pre-selected asset set's type so the user lands on the right tab.
  // Applied during render rather than in an effect: looking the asset set up is
  // a pure search over data already in hand, and doing it in an effect committed
  // one render on the default tab with nothing selected before snapping to the
  // pre-selected set. The latch is state rather than a ref for the same reason —
  // a ref written during render is not safe, and the effect it used to live in
  // is gone.
  const [appliedPreselect, setAppliedPreselect] = useState<string | null>(null)
  if (preselectId && assetSetsQuery.data && appliedPreselect !== preselectId) {
    const found = assetSetsQuery.data.find((a) => a.id === preselectId)
    if (found) {
      setAppliedPreselect(preselectId)
      if (found.assetSetType === AssetSetType.DroneImages) {
        setSelectedCompareType('image-comparison')
      } else if (found.assetSetType === AssetSetType.OdmOutputs) {
        setSelectedCompareType('map')
      }
      setSelectedAssetSets([preselectId])
    }
  }

  // Fetch assets from selected asset sets for map comparison
  const leftAssetSetAssetsQuery = useQuery({
    queryKey: ['assets', selectedAssetSets[0]],
    queryFn: async () => {
      if (!selectedAssetSets[0]) return []
      const response = await assetClient.listAssetsByAssetSet(
        {
          assetSetId: selectedAssetSets[0],
          presignedUrlType: PresignedUrlType.GET,
        },
        metadata,
      )
      return response?.items || []
    },
    enabled: selectedCompareType === 'map' && !!selectedAssetSets[0],
  })

  const rightAssetSetAssetsQuery = useQuery({
    queryKey: ['assets', selectedAssetSets[1]],
    queryFn: async () => {
      if (!selectedAssetSets[1]) return []
      const response = await assetClient.listAssetsByAssetSet(
        {
          assetSetId: selectedAssetSets[1],
          presignedUrlType: PresignedUrlType.GET,
        },
        metadata,
      )
      return response?.items || []
    },
    enabled: selectedCompareType === 'map' && !!selectedAssetSets[1],
  })

  const handleAssetSetSelection = (assetSetId: string) => {
    setSelectedAssetSets((prev) => {
      // For image comparison, allow single selection (toggle between 0, 1, and 2 selections)
      if (selectedCompareType === 'image-comparison') {
        if (prev.includes(assetSetId)) {
          // If clicking on already selected, deselect it
          return prev.filter((id) => id !== assetSetId)
        } else if (prev.length === 0) {
          // First selection
          return [assetSetId]
        } else if (prev.length === 1) {
          // Adding second selection
          return [...prev, assetSetId]
        } else {
          // Already have 2, replace the first with new
          return [prev[1], assetSetId]
        }
      } else {
        // For map comparison, keep existing logic (require 2 selections)
        const newSelection = prev.includes(assetSetId)
          ? prev.filter((id) => id !== assetSetId)
          : prev.length < 2
            ? [...prev, assetSetId]
            : [prev[1], assetSetId] // Replace the first selection with the new one

        // Reset asset selection when asset sets change
        setSelectedAssets({})
        return newSelection
      }
    })
  }

  const handleAssetSelection = (
    assetId: string,
    position: 'left' | 'right',
  ) => {
    setSelectedAssets((prev) => ({
      ...prev,
      [position]: assetId,
    }))
  }

  const handleNext = () => {
    if (selectedCompareType === 'image-comparison') {
      handleCompare()
    } else {
      setStep('assets')
    }
  }

  const handleBack = () => {
    setStep('asset-sets')
    setSelectedAssets({})
  }

  const handleCompare = () => {
    if (selectedCompareType === 'image-comparison') {
      if (selectedAssetSets.length < 1) return
      // If only one asset set is selected, use it for both sides
      const assetIds =
        selectedAssetSets.length === 1
          ? `${selectedAssetSets[0]},${selectedAssetSets[0]}`
          : selectedAssetSets.join(',')
      navigate({
        to: '/orgs/$orgId/projects/$projectId/compare/$compareType',
        params: {
          orgId,
          projectId,
          compareType: selectedCompareType,
        },
        search: {
          assetIds,
        },
      })
    } else {
      if (!selectedAssets.left || !selectedAssets.right) return
      const assetIds = [selectedAssets.left, selectedAssets.right].join(',')
      navigate({
        to: '/orgs/$orgId/projects/$projectId/compare/$compareType',
        params: {
          orgId,
          projectId,
          compareType: selectedCompareType,
        },
        search: {
          assetIds,
        },
      })
    }
  }

  const getMapCompatibleAssets = (assets: AssetWithPresignedUrl[]) => {
    const compatibleTypes = [AssetType.Cog, AssetType.GeoTiff, AssetType.Tiff]
    return assets.filter((asset) =>
      compatibleTypes.includes(asset.assetType as AssetType),
    )
  }

  const getFilteredAssetSets = () => {
    if (!assetSetsQuery.data) return []

    if (selectedCompareType === 'image-comparison') {
      return assetSetsQuery.data.filter(
        (assetSet) => assetSet.assetSetType === AssetSetType.DroneImages,
      )
    } else {
      return assetSetsQuery.data.filter(
        (assetSet) => assetSet.assetSetType === AssetSetType.OdmOutputs,
      )
    }
  }

  const filteredAssetSets = getFilteredAssetSets()

  return (
    <div>
      <div className="space-y-6">
        <div>
          <h1 className="text-text text-xl font-semibold mb-2">
            Compare Asset Sets
          </h1>
          <p className="text-text/75 text-sm">
            Select comparison type and choose asset sets to compare.
          </p>
        </div>

        {step === 'asset-sets' && (
          <div className="w-full bg-panel rounded-lg shadow-lg overflow-hidden">
            <div className="flex justify-between items-center p-4 border-b border-border">
              <div className="flex items-center space-x-6">
                <h1 className="text-text text-xl font-semibold">
                  Select Asset Sets (
                  <span className="font-mono">{selectedAssetSets.length}</span>)
                </h1>
                <Tabs
                  value={selectedCompareType}
                  onValueChange={(value) => {
                    setSelectedCompareType(value as CompareType)
                    setSelectedAssetSets([])
                    setSelectedAssets({})
                    setStep('asset-sets')
                  }}
                  className="bg-panel"
                >
                  <TabsList className="bg-panel-2">
                    <TabsTrigger value="map">Map Comparison</TabsTrigger>
                    <TabsTrigger value="image-comparison">
                      Image Comparison
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>
              <div className="flex space-x-3">
                <Button
                  variant="secondary"
                  onClick={() =>
                    navigate({ to: `/orgs/${orgId}/projects/${projectId}` })
                  }
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleNext}
                  disabled={
                    selectedCompareType === 'image-comparison'
                      ? selectedAssetSets.length < 1
                      : selectedAssetSets.length < 2
                  }
                >
                  {selectedCompareType === 'image-comparison'
                    ? 'Compare Images'
                    : 'Next: Select Assets'}
                </Button>
              </div>
            </div>

            {filteredAssetSets.length === 0 ? (
              <div className="text-center py-8 text-text/50">
                No compatible asset sets found for{' '}
                {selectedCompareType === 'image-comparison' ? 'image' : 'map'}{' '}
                comparison.
              </div>
            ) : (
              <div className="p-6 max-h-[calc(100vh-19rem)]  overflow-y-auto">
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                  {filteredAssetSets.map((assetSet) => (
                    <div
                      key={assetSet.id}
                      className={`border rounded-lg p-6 bg-panel-2 cursor-pointer transition-all hover:shadow-md ${
                        selectedAssetSets.includes(assetSet.id)
                          ? 'border-accent bg-accent/5 shadow-md'
                          : 'border-border hover:border-border/50'
                      }`}
                      onClick={() => handleAssetSetSelection(assetSet.id)}
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-lg truncate">
                            {assetSet.name}
                          </h4>
                          <p className="text-sm text-accent font-medium mt-1">
                            {assetSet.assetSetType === AssetSetType.DroneImages
                              ? 'Drone Images'
                              : 'ODM Outputs'}
                          </p>
                        </div>
                        {selectedAssetSets.includes(assetSet.id) && (
                          <div
                            className={`w-8 h-8 rounded-full bg-accent flex items-center justify-center font-mono flex-shrink-0 ml-3 ${
                              selectedCompareType === 'image-comparison' &&
                              selectedAssetSets.length === 1
                                ? 'text-xs'
                                : 'text-sm'
                            }`}
                          >
                            <span className="text-white font-bold">
                              {selectedCompareType === 'image-comparison' &&
                              selectedAssetSets.length === 1
                                ? '1|2'
                                : selectedAssetSets.indexOf(assetSet.id) + 1}
                            </span>
                          </div>
                        )}
                      </div>

                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-text/60">Asset Set ID:</span>
                          <span className="text-text/80 font-mono text-xs">
                            {assetSet.id}
                          </span>
                        </div>

                        <div className="flex justify-between">
                          <span className="text-text/60">Created:</span>
                          <span className="text-text/80">
                            {assetSet.created.toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                            })}
                          </span>
                        </div>

                        <div className="flex justify-between">
                          <span className="text-text/60">Size:</span>
                          <span className="text-text/80">
                            {assetSet.storageSummary
                              ? `${((assetSet.storageSummary.standard + assetSet.storageSummary.standardIA + assetSet.storageSummary.oneZoneIA + assetSet.storageSummary.glacierIR + assetSet.storageSummary.glacierFlex + assetSet.storageSummary.glacierDA) / (1024 * 1024 * 1024)).toFixed(2)} GB`
                              : 'Unknown'}
                          </span>
                        </div>

                        {assetSet.containedAssetTypes &&
                          assetSet.containedAssetTypes.length > 0 && (
                            <div>
                              <span className="text-text/60 block mb-1">
                                Asset Types:
                              </span>
                              <div className="flex flex-wrap gap-1">
                                {assetSet.containedAssetTypes
                                  .slice(0, 3)
                                  .map((type) => (
                                    <span
                                      key={type}
                                      className="px-2 py-1 bg-panel-3 rounded text-xs font-medium"
                                    >
                                      {type.replace('_', ' ')}
                                    </span>
                                  ))}
                                {assetSet.containedAssetTypes.length > 3 && (
                                  <span className="px-2 py-1 bg-panel-2 rounded text-xs">
                                    +{assetSet.containedAssetTypes.length - 3}{' '}
                                    more
                                  </span>
                                )}
                              </div>
                            </div>
                          )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {step === 'assets' && selectedCompareType === 'map' && (
          <div className="w-full bg-panel rounded-lg shadow-lg overflow-hidden">
            <div className="flex justify-between items-center p-4 border-b border-border">
              <div className="sm:flex-auto">
                <h1 className="text-text text-xl font-semibold">
                  Select Assets to Compare
                </h1>
              </div>
              <div className="flex space-x-3">
                <Button variant="secondary" onClick={handleBack}>
                  Back to Asset Sets
                </Button>
                <Button
                  variant="secondary"
                  onClick={() =>
                    navigate({ to: `/orgs/${orgId}/projects/${projectId}` })
                  }
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleCompare}
                  disabled={!selectedAssets.left || !selectedAssets.right}
                >
                  Compare Selected Assets
                </Button>
              </div>
            </div>
            <div className="p-6 max-h-[calc(100vh-19rem)] min-h-[16rem] overflow-y-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Left Asset Selection */}
                <div>
                  <h4 className="font-medium mb-4">
                    Left Asset (
                    {
                      assetSetsQuery.data?.find(
                        (as) => as.id === selectedAssetSets[0],
                      )?.name
                    }
                    )
                  </h4>
                  <div className="space-y-3">
                    {leftAssetSetAssetsQuery.data &&
                      getMapCompatibleAssets(leftAssetSetAssetsQuery.data).map(
                        (asset) => (
                          <div
                            key={asset.id}
                            className={`border rounded-lg p-4 cursor-pointer transition-all ${
                              selectedAssets.left === asset.id
                                ? 'border-accent bg-accent/5'
                                : 'border-border hover:border-border/50'
                            }`}
                            onClick={() =>
                              handleAssetSelection(asset.id, 'left')
                            }
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex-1 min-w-0">
                                <h5 className="font-medium truncate">
                                  {asset.name}
                                </h5>
                                <p className="text-sm text-text/75">
                                  {asset.assetType}
                                </p>
                                <p className="text-xs text-text/50">
                                  {asset.size
                                    ? (asset.size / (1024 * 1024)).toFixed(2)
                                    : '0'}{' '}
                                  MB
                                </p>
                              </div>
                              {selectedAssets.left === asset.id && (
                                <div className="w-6 h-6 rounded-full bg-accent flex items-center justify-center">
                                  <span className="text-white text-sm">✓</span>
                                </div>
                              )}
                            </div>
                          </div>
                        ),
                      )}
                    {leftAssetSetAssetsQuery.data &&
                      getMapCompatibleAssets(leftAssetSetAssetsQuery.data)
                        .length === 0 && (
                        <div className="text-center py-4 text-text/50">
                          No map-compatible assets found
                        </div>
                      )}
                  </div>
                </div>

                {/* Right Asset Selection */}
                <div>
                  <h4 className="font-medium mb-4">
                    Right Asset (
                    {
                      assetSetsQuery.data?.find(
                        (as) => as.id === selectedAssetSets[1],
                      )?.name
                    }
                    )
                  </h4>
                  <div className="space-y-3">
                    {rightAssetSetAssetsQuery.data &&
                      getMapCompatibleAssets(rightAssetSetAssetsQuery.data).map(
                        (asset) => (
                          <div
                            key={asset.id}
                            className={`border rounded-lg p-4 cursor-pointer transition-all ${
                              selectedAssets.right === asset.id
                                ? 'border-accent bg-accent/5'
                                : 'border-border hover:border-border/50'
                            }`}
                            onClick={() =>
                              handleAssetSelection(asset.id, 'right')
                            }
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex-1 min-w-0">
                                <h5 className="font-medium truncate">
                                  {asset.name}
                                </h5>
                                <p className="text-sm text-text/75">
                                  {asset.assetType}
                                </p>
                                <p className="text-xs text-text/50">
                                  {asset.size
                                    ? (asset.size / (1024 * 1024)).toFixed(2)
                                    : '0'}{' '}
                                  MB
                                </p>
                              </div>
                              {selectedAssets.right === asset.id && (
                                <div className="w-6 h-6 rounded-full bg-accent flex items-center justify-center">
                                  <span className="text-white text-sm">✓</span>
                                </div>
                              )}
                            </div>
                          </div>
                        ),
                      )}
                    {rightAssetSetAssetsQuery.data &&
                      getMapCompatibleAssets(rightAssetSetAssetsQuery.data)
                        .length === 0 && (
                        <div className="text-center py-4 text-text/50">
                          No map-compatible assets found
                        </div>
                      )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
